<script lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import { defineComponent } from 'vue'

/**
 * ElementVisibility is a Vue component that uses the Intersection Observer API to determine
 * when its content becomes visible in the viewport. It provides a slot with a prop `isVisible`
 * that indicates whether the content is currently visible. The component also removes the
 * trigger element from the DOM once it becomes visible to optimize performance.
 */
export default defineComponent({
  props: {
    /**
     * The root margin for the Intersection Observer. This allows you to trigger visibility
     * slightly before the element is fully in view. The default is '200px'.
     *
     * @type {string}
     * @default '200px'
     */
    rootMargin: {
      type: String,
      default: '200px',
    },
  },
  data() {
    return {
      isVisible: false,
      stop: null as (() => void) | null,
    }
  },
  mounted() {
    this.setupIntersectionObserver()
  },
  beforeUnmount() {
    if (this.stop) {
      this.stop()
    }
  },
  methods: {
    setupIntersectionObserver() {
      const { stop, isSupported } = useIntersectionObserver(
        () => this.$refs.visibilityTrigger,
        ([{ isIntersecting }]) => {
          if (isIntersecting) {
            // Update the visibility state
            this.isVisible = true
            // Remove the trigger element from the DOM
            this.$refs.visibilityTrigger.remove()
            // Stop observing since we only care about the first intersection
            stop()
          }
        },
        {
          rootMargin: this.rootMargin, // Trigger slightly before the element is fully in view
        }
      )

      if (!isSupported.value) {
        // If Intersection Observer is not supported, fallback to showing the content immediately
        this.isVisible = true
        return
      }

      this.stop = stop
    },
  },
})
</script>

<template>
  <!-- Use standard CSS to hide the trigger element & prevent possible FOUC -->
  <i ref="visibilityTrigger" style="visibility: hidden; width: 0; height: 0" />
  <slot v-if="isVisible"></slot>
</template>
